import { createFileRoute } from '@tanstack/react-router';
import { motion } from 'motion/react';
import {
  User,
  Edit,
  Save,
  Loader2,
  CheckCircle,
  CreditCard,
  ExternalLink,
} from 'lucide-react';
import { useState, useEffect } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '@/firebase/config';
import { toast } from 'sonner';

import { useAuth } from '@/contexts/AuthContext';
import ProtectedRoute from '@/components/auth/ProtectedRoute';
import {
  useUserProfile,
  useCompleteUserData,
} from '@/hooks/useUserProfile';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';

import type { UpdateUserProfileRequest } from '../../shared/types/user';

export const Route = createFileRoute('/profile')({
  component: ProfileComponent,
});

function ProfileComponent() {
  return (
    <ProtectedRoute>
      <ProfileContent />
    </ProtectedRoute>
  );
}

function ProfileContent() {
  const { currentUser } = useAuth();
  const {
    profile,
    isLoading: profileLoading,
    updateProfile,
    isUpdating: profileUpdating,
    updateError: profileError,
  } = useUserProfile();
  const {
    userData,
    isLoading: userDataLoading,
    initializeUserData,
    isInitializing,
  } = useCompleteUserData();

  // Form state
  const [isEditing, setIsEditing] = useState(false);
  const [profileForm, setProfileForm] = useState({
    displayName: '',
    phoneNumber: '',
  });

  // Initialize user data if not exists
  useEffect(() => {
    if (currentUser && !userData && !userDataLoading) {
      initializeUserData({
        email: currentUser.email!,
        displayName:
          currentUser.displayName || currentUser.email!.split('@')[0],
      });
    }
  }, [currentUser, userData, userDataLoading, initializeUserData]);

  // Update form when profile data loads
  useEffect(() => {
    if (profile) {
      setProfileForm({
        displayName: profile.displayName || '',
        phoneNumber: profile.phoneNumber || '',
      });
    }
  }, [profile]);

  const [portalLoading, setPortalLoading] = useState(false);

  const handleManageSubscription = async () => {
    setPortalLoading(true);
    try {
      const createBillingPortalSession = httpsCallable(
        functions!,
        'createBillingPortalSession'
      );
      const result = await createBillingPortalSession({
        returnUrl: window.location.href,
      });
      const { url } = result.data as { url: string };
      window.location.href = url;
    } catch (err) {
      toast.error('Could not open subscription management. Please try again.');
    } finally {
      setPortalLoading(false);
    }
  };

  const handleProfileSave = () => {
    const updates: UpdateUserProfileRequest = {
      displayName: profileForm.displayName,
      phoneNumber: profileForm.phoneNumber,
    };
    updateProfile(updates);
    setIsEditing(false);
  };

  const isLoading = profileLoading || userDataLoading || isInitializing;

  if (isLoading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="flex flex-col items-center space-y-4">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <p className="text-muted-foreground">Loading your profile...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 max-w-4xl">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-foreground mb-2">
            Profile Settings
          </h1>
          <p className="text-muted-foreground">
            Manage your account information
          </p>
        </div>

        {/* Subscription */}
        {profile?.subscriptionStatus && (
          <Card className="mb-6">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <CreditCard className="h-5 w-5" />
                  Subscription
                </CardTitle>
                <CardDescription>
                  Manage your PlotMyTravel subscription
                </CardDescription>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={handleManageSubscription}
                disabled={portalLoading}
                className="flex items-center gap-2"
              >
                {portalLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <ExternalLink className="h-4 w-4" />
                )}
                Manage Subscription
              </Button>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-3">
                <Badge
                  className={
                    profile.subscriptionStatus === 'active'
                      ? 'bg-green-100 text-green-800 border-green-200'
                      : profile.subscriptionStatus === 'past_due'
                        ? 'bg-yellow-100 text-yellow-800 border-yellow-200'
                        : 'bg-muted text-muted-foreground'
                  }
                >
                  {profile.subscriptionStatus === 'active'
                    ? 'Active'
                    : profile.subscriptionStatus === 'past_due'
                      ? 'Past Due'
                      : 'Canceled'}
                </Badge>
                <span className="text-sm text-muted-foreground">
                  {profile.subscriptionType === 'annual'
                    ? 'Annual plan — $105/yr'
                    : 'Monthly plan — $11/mo'}
                </span>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Account Information */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <User className="h-5 w-5" />
                Account Information
              </CardTitle>
              <CardDescription>
                Update your personal information and contact details
              </CardDescription>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsEditing(!isEditing)}
              className="flex items-center space-x-2"
            >
              <Edit className="h-4 w-4" />
              <span>{isEditing ? 'Cancel' : 'Edit'}</span>
            </Button>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-foreground mb-2 block">
                  Display Name
                </label>
                {isEditing ? (
                  <Input
                    value={profileForm.displayName}
                    onChange={e =>
                      setProfileForm(prev => ({
                        ...prev,
                        displayName: e.target.value,
                      }))
                    }
                    placeholder="Enter your display name"
                  />
                ) : (
                  <p className="text-foreground py-2 px-3 bg-muted rounded-md">
                    {profile?.displayName || 'Not set'}
                  </p>
                )}
              </div>

              <div>
                <label className="text-sm font-medium text-foreground mb-2 block">
                  Email Address
                </label>
                <div className="flex items-center space-x-2">
                  <p className="text-foreground py-2 px-3 bg-muted rounded-md flex-1">
                    {currentUser?.email}
                  </p>
                  {currentUser?.emailVerified && (
                    <CheckCircle className="h-5 w-5 text-green-500" />
                  )}
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-foreground mb-2 block">
                  Phone Number
                </label>
                {isEditing ? (
                  <Input
                    value={profileForm.phoneNumber}
                    onChange={e =>
                      setProfileForm(prev => ({
                        ...prev,
                        phoneNumber: e.target.value,
                      }))
                    }
                    placeholder="Enter your phone number"
                  />
                ) : (
                  <p className="text-foreground py-2 px-3 bg-muted rounded-md">
                    {profile?.phoneNumber || 'Not set'}
                  </p>
                )}
              </div>
            </div>

            {isEditing && (
              <div className="flex justify-end space-x-2 pt-4 border-t">
                <Button
                  variant="outline"
                  onClick={() => setIsEditing(false)}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleProfileSave}
                  disabled={profileUpdating}
                  className="flex items-center space-x-2"
                >
                  {profileUpdating && (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  )}
                  <Save className="h-4 w-4" />
                  <span>Save Changes</span>
                </Button>
              </div>
            )}

            {profileError && (
              <div className="text-red-600 text-sm mt-2">
                Error updating profile. Please try again.
              </div>
            )}
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
}
